import intersectionBy from 'lodash/intersectionBy';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import { fetchCertificationCategories } from '../certification_categories';
import * as geographyTypes from '../geographies/types';
import * as types from './types';
import { dropdownFilters, filters, filtersTransformer } from '../shared/filters';
import { APPLY_SAVED_FILTER_ID } from './types';
import { clone, isArray } from 'lodash';
import { snakeCase } from 'snake-case';
import filter from 'lodash/filter';
import {
  DEFAULT_DATE_RANGE as filterToApply,
  DEFAULT_DATE_RANGE as option,
} from 'contexts/EmployerInterviewContext';
export { dropdownFilters } from '../shared/filters';

const initialState = {
  loading: false,
  loadingCandidate: false,
  candidates: {},
  profiles: [],
  filters,
  filtersSearched: false,
  dropdownFilters,
  showRecommendations: false,
  recommendations: {
    jobId: null,
    locationId: null,
  },
  appliedSavedFilterId: null,
};

const candidatesReducer = (state = cloneDeep(initialState), { type, payload, error }) => {
  switch (type) {
    case geographyTypes.GEOGRAPHY_OPTIONS_LOAD_SUCCESS: {
      const newFilters = filtersTransformer(payload, state.filters);

      return {
        ...state,
        filters: [...newFilters],
      };
    }

    case fetchCertificationCategories.fulfilled.type: {
      const filteredArr = payload.filter((certCat) =>
        ['state_certification', 'cda'].includes(certCat.categoryType),
      );
      const certificateOptions = filteredArr.flatMap((category) => {
        const geographyName = category.geography()?.longName;
        const optionGroup = category.title;
        return category
          .certificationOptions()
          .toArray()
          .map((certOption) => ({
            id: certOption.id,
            group: optionGroup,
            value: certOption.title,
            label: certOption.title,
            geography: geographyName,
          }));
      });

      const sortedCertificateOptions = sortBy(certificateOptions, ['geography', 'group']);
      const certificationFilter = state.filters.find((filter) => filter.id === 'certifications');

      certificationFilter.options = certificationFilter.options.length
        ? intersectionBy(certificationFilter.options, sortedCertificateOptions, 'id')
        : sortedCertificateOptions;

      return {
        ...state,
        filters: [...state.filters],
      };
    }
    case types.CANDIDATE_LOAD_START:
      return {
        ...state,
        loadingCandidate: true,
      };

    case types.CANDIDATE_LOAD_SUCCESS:
      const { candidates } = state;
      if (payload?.slug) {
        candidates[payload.slug] = payload;
      }
      return {
        ...state,
        candidates,
        loadingCandidate: false,
      };
    case types.CANDIDATE_LOAD_FAILURE:
      return {
        ...state,
        loadingCandidate: false,
      };
    case types.CANDIDATE_SEARCH_CHANGE_FILTER: {
      const filters = state.filters;
      const section = filters.find((filter) => filter.id === payload.section.id);
      let { option } = payload;

      if (section.type === 'checkbox') {
        if (section.id !== 'languages') {
          option = section.options.find(
            (option) =>
              option.value === payload.option.value &&
              option.geography === payload.option.geography,
          );
          option.checked = !option.checked;
        } else {
          if (isArray(option)) {
            const selectedIds = option.map((opt) => opt.value);
            section.options.forEach((opt) => {
              opt.checked = selectedIds.includes(opt.value);
            });
          } else {
            // in this case, we are sending the option from the filter drawer 'removeFilter' function
            option.checked = false;
          }
        }
      } else {
        section.value = option;
      }

      return {
        ...state,
        // change obj id to make memos that depend on this work
        filters: clone(filters),
        filtersSearched: false,
      };
    }
    case types.CANDIDATE_MESSAGED: {
      const id = payload;
      const profiles = [...state.profiles];
      const messagedProfile = profiles.find((profile) => profile.id === id);

      messagedProfile && (messagedProfile.previouslyMessaged = true);

      return {
        ...state,
        profiles,
      };
    }
    case types.CANDIDATE_SEARCH_RESET_FILTER: {
      const resFilters = cloneDeep(state.filters);
      resFilters.forEach((filter) => {
        switch (filter.type) {
          case 'checkbox':
            filter.options.forEach((option) => {
              option.checked = false;
            });
            break;
          case 'complex':
            filter.value = initialState.filters.find((f) => f.id === filter.id)?.value || '';
            break;
          case 'text':
          default:
            filter.value = '';
        }
      });

      return {
        ...state,
        filters: resFilters,
        filtersSearched: false,
        profiles: [],
        loading: true,
      };
    }
    case types.CANDIDATES_FETCH_PENDING: {
      return {
        ...state,
        loading: true,
      };
    }
    case types.CANDIDATES_FETCH_FULFILLED: {
      return {
        ...state,
        nextPage: payload.pagination.next_page,
        totalPages: payload.pagination.total_pages,
        currentPage: payload.pagination.current_page,
        profiles: payload.candidates,
        loading: false,
      };
    }
    case types.CANDIDATES_FETCH_REJECTED: {
      if (error?.name === 'AbortError') {
        return state;
      }
      console.error('Error running request', error);
      return {
        ...state,
        loading: false,
      };
    }
    case types.RECOMMENDATIONS_PARAMETERS_UPDATED: {
      return {
        ...state,
        recommendations: {
          ...state.recommendations,
          jobId: payload.jobId,
          locationId: payload.locationId,
        },
      };
    }

    case types.METADA_FULFILLED: {
      const profiles = _.cloneDeep(state.profiles);

      payload.forEach((metadata) => {
        const profile = profiles.find((profile) => profile.id === metadata.id);
        if (profile) {
          profile.metadata = metadata;
        }
      });

      return {
        ...state,
        profiles,
      };
    }

    case types.UPDATE_DROPDOWN_FILTER: {
      return {
        ...state,
        dropdownFilters: state.dropdownFilters.map((filter) => ({
          ...filter,
          selected: payload.id === filter.id,
        })),
      };
    }

    case types.UPDATE_SHOW_RECOMMENDATIONS: {
      return {
        ...state,
        showRecommendations: payload,
      };
    }

    case types.APPLY_SAVED_FILTER_ID: {
      return {
        ...state,
        appliedSavedFilterId: payload,
      };
    }
    case types.CANDIDATE_APPLY_SAVED_FILTER: {
      const defaultizeFilters = (filters) => {
        filters.forEach((filter) => {
          if (filter.type === 'checkbox') {
            filter.options.forEach((option) => (option.checked = false));
          } else if (filter.type === 'text') {
            filter.value = '';
          } else if (filter.type === 'complex' && filter.id === 'distance') {
            filter.value = {
              lat: undefined,
              lng: undefined,
              radiusInMiles: undefined,
            };
          }
        });

        return filters;
      };

      const defaultFilters = defaultizeFilters(cloneDeep(state.filters));
      const savedFilter = payload;

      Object.entries(savedFilter.data).forEach(([k, v]) => {
        const filterKey = snakeCase(k);

        // Apply the filter of each of these values to the defaultFilters
        const filterToApply = defaultFilters.find((filter) => filter.id === filterKey);
        if (filterToApply) {
          if (filterToApply.type === 'checkbox') {
            filterToApply.options.forEach((option) => {
              if (v.includes(option.value)) {
                option.checked = true;
              }
            });
          } else if (filterToApply.type === 'text') {
            filterToApply.value = v;
          } else if (filterToApply.type === 'complex' && filterToApply.id === 'distance') {
            filterToApply.value = v;
          }
        }
      });

      return {
        ...state,
        filters: defaultFilters,
      };
    }

    case types.MARK_FILTERS_AS_SEARCHED: {
      return {
        ...state,
        filtersSearched: true,
      };
    }

    default:
      return state;
  }
};

export default candidatesReducer;
